// Quality indicators (docs/plans/phase3-khis-and-quality.md §1.2, decisions Q1–Q9): the
// definitions and the arithmetic. Every indicator is computed from data Wodi already holds.
// No target or threshold lives here: a target is a facility setting, empty until set
// (CLAUDE.md clinical safety rules). Only the direction (whether lower or higher is better)
// is part of a definition.

import { waitingStats } from '../reports/summary.ts';
import { eatDayOf, type InstantRange } from '../time.ts';
import type { KhisPeriod } from '../khis/index.ts';

export const QUALITY_INDICATORS = {
  wait_to_consultation: {
    title: 'Waiting time, arrival to consultation',
    unit: 'minutes',
    better: 'lower',
    perMonth: true,
    definition:
      'For each outpatient, walk-in or casualty visit that started in the month: the time from arrival to the first time a consultation point started seeing the patient. Visits never seen at a consultation point are left out. Median and 9 in 10.',
  },
  length_of_stay: {
    title: 'Length of stay',
    unit: 'days',
    better: 'lower',
    perMonth: true,
    definition:
      'For each admission discharged in the month, whatever the disposition: the time from admission to discharge. Cancelled admissions are left out. Median, with the mean beside it.',
  },
  bed_occupancy: {
    title: 'Bed occupancy',
    unit: 'percent',
    better: 'higher',
    perMonth: true,
    definition:
      'Time patients were assigned to beds in the month, divided by the time beds were available: beds in active wards, not retired, counted from when each bed was added. A bed blocked for part of the month still counts as available.',
  },
  lab_turnaround: {
    title: 'Lab turnaround',
    unit: 'minutes',
    better: 'lower',
    perMonth: true,
    definition:
      'For each lab request whose first report was released in the month: the time from the first sample collection to that release. Median and 9 in 10, and the share released within the test’s own turnaround target where the lab set one.',
  },
  critical_ack_time: {
    title: 'Critical result acknowledgement',
    unit: 'minutes',
    better: 'lower',
    perMonth: true,
    definition:
      'For each critical lab result notification raised in the month: the time from the notification to the clinician’s acknowledgement. Notifications not yet acknowledged, and those the lab closed after phoning, are counted beside it.',
  },
  claim_rejection_rate: {
    title: 'SHA claim rejection rate',
    unit: 'percent',
    better: 'lower',
    perMonth: true,
    definition:
      'Of SHA claims whose first decision (approved, partly approved, rejected or paid) came in the month, the percent whose first decision was a rejection. Claims entered in error are left out.',
  },
  stock_outs: {
    title: 'Items out of stock now',
    unit: 'count',
    better: 'lower',
    perMonth: false,
    definition:
      'Items with a reorder level set at one of the facility’s stores that have no usable stock there (none, or only expired batches), at the time the dashboard was read. Each store and item counts once. This is not kept per month.',
  },
  readmissions_30d: {
    title: '30-day readmissions',
    unit: 'percent',
    better: 'lower',
    perMonth: true,
    definition:
      'Of patients discharged alive in the month, the percent admitted again at the same facility within 30 days of discharge. Planned and unplanned readmissions are not told apart. While 30 days have not passed since a discharge, the month is still counting.',
  },
} as const satisfies Record<
  string,
  {
    title: string;
    unit: 'minutes' | 'days' | 'percent' | 'count';
    better: 'lower' | 'higher';
    perMonth: boolean;
    definition: string;
  }
>;

export type QualityIndicatorKey = keyof typeof QUALITY_INDICATORS;
export const QUALITY_INDICATOR_KEYS = Object.keys(
  QUALITY_INDICATORS,
) as readonly QualityIndicatorKey[];

export function isQualityIndicatorKey(value: string): value is QualityIndicatorKey {
  return Object.hasOwn(QUALITY_INDICATORS, value);
}

// --- Months ----------------------------------------------------------------------------------

/** The `count` months ending with `last`, oldest first. */
export function monthsEnding(last: KhisPeriod, count: number): KhisPeriod[] {
  const months: KhisPeriod[] = [];
  let { year, month } = last;
  for (let i = 0; i < count; i += 1) {
    months.unshift({ year, month });
    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
  }
  return months;
}

/** The East Africa month an instant falls in. */
export function monthOf(instant: Date): KhisPeriod {
  const day = eatDayOf(instant);
  return { year: day.year, month: day.month };
}

// --- Arithmetic ------------------------------------------------------------------------------

const MILLIS_PER_MINUTE = 60_000;
const MILLIS_PER_DAY = 86_400_000;
const READMISSION_WINDOW_MS = 30 * MILLIS_PER_DAY;

function oneDecimal(value: number): number {
  return Math.round(value * 10) / 10;
}

export interface DurationSummary {
  readonly count: number;
  /** Whole minutes; null with nothing to measure. */
  readonly median: number | null;
  readonly p90: number | null;
}

/** Durations in milliseconds, summarised the way the day summary summarises waits. */
export function durationSummary(millis: readonly number[]): DurationSummary {
  const stats = waitingStats(millis.map((ms) => ms / MILLIS_PER_MINUTE));
  return { count: stats.count, median: stats.medianMinutes, p90: stats.p90Minutes };
}

export interface LengthOfStaySummary {
  readonly count: number;
  readonly medianDays: number | null;
  readonly meanDays: number | null;
}

export function lengthOfStaySummary(millis: readonly number[]): LengthOfStaySummary {
  if (millis.length === 0) return { count: 0, medianDays: null, meanDays: null };
  const days = millis.map((ms) => Math.max(0, ms) / MILLIS_PER_DAY).sort((a, b) => a - b);
  const median = days[Math.ceil(days.length / 2) - 1] ?? 0;
  const mean = days.reduce((s, d) => s + d, 0) / days.length;
  return { count: days.length, medianDays: oneDecimal(median), meanDays: oneDecimal(mean) };
}

/** A percent to one decimal; null when there is nothing to divide by. */
export function percentOf(part: number, whole: number): number | null {
  if (whole === 0) return null;
  return oneDecimal((part / whole) * 100);
}

function overlapMs(from: Date, to: Date | null, range: InstantRange, now: Date): number {
  const end = Math.min(
    to?.getTime() ?? Number.POSITIVE_INFINITY,
    range.endExclusive.getTime(),
    now.getTime(),
  );
  const start = Math.max(from.getTime(), range.startInclusive.getTime());
  return Math.max(0, end - start);
}

/** Occupied bed time over available bed time in `range`, up to `now` (plan Q5). */
export function bedOccupancyPercent(input: {
  readonly beds: readonly { readonly availableFrom: Date }[];
  readonly assignments: readonly { readonly startedAt: Date; readonly endedAt: Date | null }[];
  readonly range: InstantRange;
  readonly now: Date;
}): number | null {
  const available = input.beds.reduce(
    (sum, b) => sum + overlapMs(b.availableFrom, null, input.range, input.now),
    0,
  );
  if (available === 0) return null;
  const occupied = input.assignments.reduce(
    (sum, a) => sum + overlapMs(a.startedAt, a.endedAt, input.range, input.now),
    0,
  );
  return oneDecimal(Math.min(100, (occupied / available) * 100));
}

export interface ReadmissionSummary {
  readonly discharges: number;
  readonly readmitted: number;
  readonly percent: number | null;
  /** Some discharge's 30 days have not passed yet. */
  readonly stillCounting: boolean;
}

/** Discharges alive in `range` followed by an admission within 30 days (plan Q7). */
export function readmissionSummary(input: {
  readonly discharges: readonly {
    readonly patientId: string;
    readonly dischargedAt: Date;
    readonly died: boolean;
  }[];
  readonly admissions: readonly { readonly patientId: string; readonly admittedAt: Date }[];
  readonly range: InstantRange;
  readonly now: Date;
}): ReadmissionSummary {
  const inRange = input.discharges.filter(
    (d) =>
      !d.died &&
      d.dischargedAt.getTime() >= input.range.startInclusive.getTime() &&
      d.dischargedAt.getTime() < input.range.endExclusive.getTime(),
  );
  let readmitted = 0;
  let stillCounting = false;
  for (const d of inRange) {
    const windowEnd = d.dischargedAt.getTime() + READMISSION_WINDOW_MS;
    if (windowEnd > input.now.getTime()) stillCounting = true;
    const again = input.admissions.some(
      (a) =>
        a.patientId === d.patientId &&
        a.admittedAt.getTime() > d.dischargedAt.getTime() &&
        a.admittedAt.getTime() <= windowEnd,
    );
    if (again) readmitted += 1;
  }
  return {
    discharges: inRange.length,
    readmitted,
    percent: percentOf(readmitted, inRange.length),
    stillCounting,
  };
}

export type TargetStatus = 'met' | 'not_met' | 'no_target' | 'no_value';

/** Whether a value meets the facility's own target, in the indicator's direction (plan Q2). */
export function targetStatus(
  key: QualityIndicatorKey,
  value: number | null,
  target: number | null,
): TargetStatus {
  if (target === null) return 'no_target';
  if (value === null) return 'no_value';
  const lowerIsBetter = QUALITY_INDICATORS[key].better === 'lower';
  return (lowerIsBetter ? value <= target : value >= target) ? 'met' : 'not_met';
}
